import { useEffect, useCallback, useRef } from "react";
import { useListEnvironments, type PlainEnvironment } from "@/queries/environment-queries";
import { extractEnvironmentId, ENV_KEY_PREFIX } from "./use-environment-settings";

/**
 * Removes all localStorage entries for environments that no longer exist.
 *
 * @param activeEnvIds - A set of currently active environment IDs.
 */
function removeStaleEnvironmentSettings(activeEnvIds: Set<string>) {
    if (typeof localStorage === "undefined") {
        console.warn("localStorage is not available.");
        return;
    }

    const keysToRemove: string[] = [];

    // Collect keys that belong to environments no longer active
    for (let i = 0; i < localStorage.length; i++) {
        let key: string | null;
        try {
            key = localStorage.key(i);
        } catch (error) {
            console.warn("Error reading localStorage key at index " + i + ":", error);
            continue;
        }

        if (!key?.startsWith(ENV_KEY_PREFIX)) continue;

        const envId = extractEnvironmentId(key);
        if (envId && !activeEnvIds.has(envId)) {
            keysToRemove.push(key);
        }
    }

    // Remove each stale key individually
    for (const key of keysToRemove) {
        try {
            localStorage.removeItem(key);
        } catch (error) {
            console.warn(`Error removing localStorage key "${key}":`, error);
        }
    }
}

/**
 * Hook that performs a one-time cleanup of localStorage keys for deleted environments.
 *
 * @returns A callback function that can be used to manually trigger the cleanup.
 */
export function useEnvironmentSettingsCleanup() {
    const { data } = useListEnvironments();
    const hasCleanedUp = useRef(false);

    const performCleanup = useCallback(() => {
        const envs = data?.environments;
        if (!envs) return;

        const activeIds = new Set(envs.map((env: PlainEnvironment) => env.id));
        removeStaleEnvironmentSettings(activeIds);
    }, [data?.environments]);

    // Run cleanup only once when environments data is first loaded
    useEffect(() => {
        if (data?.environments && !hasCleanedUp.current) {
            performCleanup();
            hasCleanedUp.current = true;
        }
    }, [data?.environments, performCleanup]);

    return performCleanup;
}
